import { useState } from "react";
import { Modal } from "react-bootstrap";
import { ReactComponent as ExitIcon } from "assets/icons/NewPageSelector/exit-icon-close.svg";
import "./styles.css";
import _ from "lodash";
import ILessonPageDto from "../../../models/ILessonPageDto";
import { PageTypes } from "../../constants/pageTypes";
import { LessonPagesActions, useLessonPagesDispatch } from "../../../contexts/LessonPagesProvider/LessonPagesProvider";
import { BLACKLISTED_MICROLESSON_PAGES } from "./constants";
import { MicroLessonModalProps, MicroLessonFilter } from "./types";
import { addMicroLesson, getMicroLessonInsertionIndex, hasMicroLessonSecondaryAudio } from "./utils";
import { Main } from "./components/Main";
import { Sidebar } from "./components/Sidebar";
import { ISearchItemResponse } from "../../../models/ISearchLessonResponse";
import { useAudioManagerStore } from "../../../contexts/PageAudioManager";
import { useObjectsState } from "../../../contexts/ObjectsProvider";

export const MicroLessonModal = (props: MicroLessonModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const lessonPagesDispatch = useLessonPagesDispatch();
  const [selectedMicroLessons, setSelectedMicroLessons] = useState<ISearchItemResponse[]>([]);
  const [filters, setFilters] = useState<MicroLessonFilter[]>([]);
  const objectsState = useObjectsState();

  /**
   * Checks if any micro lesson page has secondary/alternative audio in the add response
   */
  const checkSecondaryAudioInAddResponse = (responsePages: any[]): boolean => {
    for (const item of responsePages) {
      const pages = item?.pageManifest?.Pages || [];
      if (hasMicroLessonSecondaryAudio(pages)) {
        return true;
      }
    }
    return false;
  };

  const handleCancel = () => {
    props.onHide();
  };

  const handleAdd = async (microLessons: ISearchItemResponse[]) => {
    setIsLoading(true);
    try {
      const ids = microLessons.map((lesson) => lesson.lessonId);
      const response = await addMicroLesson(ids);

      const initialPageOrder = getMicroLessonInsertionIndex(
        _.toNumber(props.currentPageOrder),
        props.lessonPages.length,
      );

      const lessonPagesCopy: ILessonPageDto[] = props.lessonPages.map((page) => ({
        ...page,
        pageManifest: _.cloneDeep((page as ILessonPageDto).pageManifest) ?? null,
      }));

      // Save the current page's state before adding the new pages
      // This prevents losing audio/objects when the lesson pages are updated
      const { narrationAudios, audioEffects } = useAudioManagerStore.getState();
      const currentPageIndex = props.currentPage;
      if (lessonPagesCopy[currentPageIndex]?.pageManifest) {
        const targetPageManifest = lessonPagesCopy[currentPageIndex].pageManifest;
        targetPageManifest.pageImage = objectsState.images;
        targetPageManifest.textBlock = objectsState.textBoxes;
        targetPageManifest.annotations = objectsState.annotations;
        if (objectsState.animatedObjects && targetPageManifest.timeline) {
          targetPageManifest.timeline.animatedObjects = objectsState.animatedObjects;
        }
        targetPageManifest.narrationAudios = narrationAudios;
        targetPageManifest.audioEffects = audioEffects;
      }

      // Also dispatch to LessonPagesProvider to keep context state in sync
      lessonPagesDispatch({
        type: LessonPagesActions.SET_OBJECTS_ON_PAGE_MANIFEST,
        payload: {
          images: objectsState.images,
          textBoxes: objectsState.textBoxes,
          annotations: objectsState.annotations,
          animatedObjects: objectsState.animatedObjects,
          narrationAudios,
          audioEffects,
        },
      });

      interface SourcePage {
        Name: string;
        Order: number;
        PageVersionId: number;
        PageId: number;
        PagePlayerType: string;
        BlobPath: string;
        PageManifest: unknown;
        LessonModeIds: number[];
        ShouldAutoPageForward: boolean;
        IsScorable: boolean;
      }

      interface ResponsePageItem {
        pageVersionId: number;
        parentPageVersionId: number;
        pageManifest: {
          MicrolessonSource?: {
            LessonId?: number;
            LessonName?: string;
            LessonVersionId?: number;
          };
          Pages: SourcePage[];
        };
      }

      response.data.pages.forEach((item: ResponsePageItem, idx: number) => {
        const pages = item.pageManifest.Pages;
        const microLessonId = item.pageManifest?.MicrolessonSource?.LessonId;
        const microLessonName = item.pageManifest?.MicrolessonSource?.LessonName;
        const pageVersionId = item.pageVersionId;
        const microLessonVersionId = item.pageManifest?.MicrolessonSource?.LessonVersionId;
        const existingIds = lessonPagesCopy.map((p) => p.uniquePageListerID || 0);
        const maxId = Math.max(...existingIds, -1);
        const nextId = maxId + 1;
        const pageOrder = initialPageOrder + idx;

        const formattedPages = pages.map((page: SourcePage) => {
          const pageType = page.PagePlayerType as PageTypes;
          return {
            blobPath: page.BlobPath,
            isScorable: page.IsScorable,
            lessonModeIds: BLACKLISTED_MICROLESSON_PAGES.includes(pageType) ? [] : page.LessonModeIds,
            name: page.Name,
            order: page.Order,
            pageManifest: page.PageManifest,
            pagePlayerType: pageType,
            pageVersionId: page.PageVersionId,
            shouldAutoPageForward: page.ShouldAutoPageForward,
          };
        });

        const newMicroLessonPage: ILessonPageDto = {
          blobPath: "",
          uniquePageListerID: nextId,
          helperReferencePage: null,
          isScorable: false,
          lessonModeIds: [3, 2, 1],
          lessonVersionId: props.lessonVersionId,
          name: microLessonName || "",
          order: pageOrder,
          pageId: microLessonId || 0,
          lessonPageId: 0,
          pageManifest: { MicrolessonSource: item.pageManifest?.MicrolessonSource, Pages: formattedPages },
          pageManifestIsDirty: true,
          pagePlayerType: "Aggregation" as PageTypes,
          pageToken: null,
          pageVersionId: pageVersionId,
          shouldAutoPageForward: false,
          isClientSideNewPage: true,
          referenceLessonId: microLessonId || 0,
          referenceLessonVersionId: microLessonVersionId || 0,
        };

        lessonPagesCopy.splice(pageOrder, 0, newMicroLessonPage);

        const dispatchPage = { ...newMicroLessonPage } as ILessonPageDto;
        lessonPagesDispatch({
          type: LessonPagesActions.ADD_NEW_PAGE,
          payload: { page: dispatchPage, pageIndex: pageOrder },
        });
      });

      const updatedPages = lessonPagesCopy.map((lesson, index) => ({
        ...lesson,
        order: index,
        lessonPageIsDirty: lesson.order !== index,
      }));

      props.setLessonPages(updatedPages);
      const lastInsertedPageOrder = initialPageOrder + response.data.pages.length - 1;
      if (response.data.pages.length > 0) {
        props.switchCurrentlyDisplayedPage(0, { aggregationOrder: lastInsertedPageOrder });
      }

      // Check if micro lesson has secondary audio and main lesson doesn't have alternative language set
      const hasSecondaryAudio = checkSecondaryAudioInAddResponse(response.data.pages);
      const hasAlternativeLanguageSet = !!props.lessonMetaData?.additionalSettings?.studentLanguages?.[1];

      // Close the modal first
      props.onHide();

      // Then trigger the alternative narration modal if needed
      if (hasSecondaryAudio && !hasAlternativeLanguageSet) {
        props.onSecondaryAudioDetected?.();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      show={props.show}
      onHide={props.onHide}
      dialogClassName="micro-lesson-modal"
      backdrop="static"
      keyboard={false}
    >
      {isLoading && <div className="loading-overlay">Adding Micro Lesson...</div>}
      <Modal.Header>
        <Modal.Title>
          <p>Add Micro Lesson</p>
        </Modal.Title>
        <ExitIcon onClick={props.onHide} />
      </Modal.Header>
      <Modal.Body>
        <div className="modal-body-container">
          <Main
            filters={filters}
            setFilters={setFilters}
            onCancel={handleCancel}
            onAdd={handleAdd}
            selectedMicroLessons={selectedMicroLessons}
            setSelectedMicroLessons={setSelectedMicroLessons}
          />
          <Sidebar selectedMicroLessons={selectedMicroLessons} onRemoveAll={() => setSelectedMicroLessons([])} />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default MicroLessonModal;
